import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminRequest, getAdminHeaders, toAssetUrl } from "./adminApi";

export default function AdminConsultants() {
  const navigate = useNavigate();
  const [consultants, setConsultants] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchConsultants();
  }, []);

  const fetchConsultants = async () => {
    try {
      setError("");
      const { res, data } = await adminRequest("/api/admin/consultants", {
        headers: getAdminHeaders(),
      });

      if (!res.ok) {
        setError(data?.detail || "加载咨询师列表失败");
        setConsultants([]);
        return;
      }

      setConsultants(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError("无法连接后台服务，请确认后端已启动");
      setConsultants([]);
    } finally {
      setLoading(false);
    }
  };

  const updateConsultant = async (consultant, action) => {
    if (action === "toggle-online" && !consultant.is_verified) {
      alert("咨询师需要先认证通过，才能开启接单");
      return;
    }

    try {
      const { res, data } = await adminRequest(
        `/api/admin/consultants/${consultant.id}/${action}`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || "操作失败");
        return;
      }

      fetchConsultants();
    } catch (err) {
      console.error(err);
      alert("操作失败");
    }
  };

  const deleteConsultant = async (consultant) => {
    const name = consultant.name || consultant.phone || `ID ${consultant.id}`;
    if (
      !window.confirm(
        `确定删除咨询师「${name}」吗？\n此操作不可恢复，关联的订单和消息也会一并删除。`
      )
    ) {
      return;
    }

    try {
      const { res, data } = await adminRequest(
        `/api/admin/consultants/${consultant.id}/delete`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || data?.msg || "删除失败");
        return;
      }

      fetchConsultants();
    } catch (err) {
      console.error(err);
      alert("删除失败");
    }
  };

  const filteredConsultants = consultants.filter((item) => {
    const text = `${item.name || ""} ${item.phone || ""} ${item.city || ""} ${
      item.specialties || ""
    }`;
    return text.toLowerCase().includes(keyword.toLowerCase());
  });

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">咨询师管理</h1>
          <p className="text-gray-400 mt-1">审核认证、上下架、查看服务资料</p>
        </div>

        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索姓名、手机号、城市"
          className="w-full md:w-80 border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>

      {error ? (
        <div className="mb-4 rounded-xl bg-red-50 text-red-600 px-4 py-3 text-sm">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-gray-400">加载中...</div>
        ) : filteredConsultants.length === 0 ? (
          <div className="p-8 text-gray-400">暂无咨询师数据</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left p-4">咨询师</th>
                  <th className="text-left p-4">城市</th>
                  <th className="text-left p-4">价格</th>
                  <th className="text-left p-4">认证</th>
                  <th className="text-left p-4">前台展示</th>
                  <th className="text-left p-4">接单</th>
                  <th className="text-right p-4">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredConsultants.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="p-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/consultants/${item.id}`)}
                        className="flex items-center gap-3 text-left hover:opacity-80"
                      >
                        <img
                          src={toAssetUrl(item.avatar) || "/default-avatar.png"}
                          alt={item.name}
                          className="w-10 h-10 rounded-full object-cover bg-gray-100"
                        />
                        <div>
                          <p className="font-bold text-gray-800 hover:text-blue-600">
                            {item.name || "未填写"}
                          </p>
                          <p className="text-gray-400">{item.phone || "-"}</p>
                        </div>
                      </button>
                    </td>
                    <td className="p-4 text-gray-600">{item.city || "-"}</td>
                    <td className="p-4 text-gray-600">
                      ¥{item.price || 0} / {item.duration || 50}分钟
                    </td>
                    <td className="p-4">
                      {item.is_verified ? (
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                          已认证
                        </span>
                      ) : (
                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                          待认证
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      {item.is_available ? (
                        <span className="text-green-600">展示中</span>
                      ) : (
                        <span className="text-gray-400">已隐藏</span>
                      )}
                    </td>
                    <td className="p-4">
                      {item.is_online ? (
                        <span className="text-green-600">在线</span>
                      ) : (
                        <span className="text-gray-400">暂停</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => updateConsultant(item, "verify")}
                          className="px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                        >
                          {item.is_verified ? "取消认证" : "认证"}
                        </button>
                        <button
                          onClick={() => updateConsultant(item, "toggle-available")}
                          className="px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                        >
                          {item.is_available ? "隐藏前台" : "前台展示"}
                        </button>
                        <button
                          onClick={() => updateConsultant(item, "toggle-online")}
                          className="px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                        >
                          {item.is_online ? "暂停接单" : "开启接单"}
                        </button>
                        <button
                          onClick={() => deleteConsultant(item)}
                          className="px-3 py-2 rounded-xl border border-red-200 text-red-500 hover:bg-red-50"
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
